/**
 * Zustands- und Zeitlogik des Aktionsknopfs – bewusst ohne React.
 *
 * Sie liegt getrennt, weil sie sich so ohne Browser prüfen lässt: die
 * Schwellen 100 ms / 400 ms / 3 s / 10 s sind das eigentliche Versprechen
 * ("kein Klick läuft ins Leere"), und ein Versprechen, das nur von Hand
 * geprüft wird, hält nicht lange.
 */

export type ActionState = 'idle' | 'busy' | 'success' | 'error' | 'unclear' | 'disabled'

/** Was während des Wartens sichtbar ist. */
export interface BusyPhase {
  /** Sofort ab Klick: Knopf gesperrt und sichtbar gedrückt. */
  locked: boolean
  /** Ab 400 ms: Ladeanzeige. Vorher nicht, sonst flackert es bei schnellen Aktionen. */
  spinner: boolean
  /** Ab 3 s: erklärender Text statt nur Drehrad. */
  explain: boolean
  /** Ab 10 s: Abbrechen oder Status prüfen anbieten – niemand darf eingesperrt sein. */
  escape: boolean
}

export const THRESHOLDS = {
  /** Sichtbare Reaktion muss innerhalb dieser Zeit erfolgen. */
  reactionMs: 100,
  spinnerMs: 400,
  explainMs: 3_000,
  escapeMs: 10_000,
  /** Wie lange eine Erfolgsmeldung am Knopf stehen bleibt. */
  successMs: 1_600
} as const

export function phaseFor(elapsedMs: number): BusyPhase {
  return {
    locked: true,
    spinner: elapsedMs >= THRESHOLDS.spinnerMs,
    explain: elapsedMs >= THRESHOLDS.explainMs,
    escape: elapsedMs >= THRESHOLDS.escapeMs
  }
}

export interface Labels {
  idle: string
  busy: string
  /** Ab 3 s – beschreibt, was gerade passiert, z.B. "Nummer wird reserviert …" */
  explain?: string
  success?: string
  error?: string
  unclear?: string
}

/**
 * Alle Beschriftungen, die der Knopf je anzeigen kann.
 *
 * Sie werden alle gleichzeitig gerendert – die inaktiven unsichtbar – damit
 * die Breite konstant bleibt. Ein Knopf, der beim Wechsel von "Senden" auf
 * "Wird gesendet …" breiter wird, lässt die halbe Seite hüpfen. Das ist die
 * Mechanik aus dem Loading Button von 21st.dev, hier ohne Fremdpaket.
 */
export function allLabels(labels: Labels): string[] {
  return [
    labels.idle,
    labels.busy,
    labels.explain ?? '',
    labels.success ?? '',
    labels.error ?? '',
    labels.unclear ?? ''
  ].filter(Boolean)
}

export function labelFor(state: ActionState, phase: BusyPhase, labels: Labels): string {
  switch (state) {
    case 'busy':
      return phase.explain ? (labels.explain ?? labels.busy) : labels.busy
    case 'success':
      return labels.success ?? labels.idle
    case 'error':
      return labels.error ?? labels.idle
    case 'unclear':
      return labels.unclear ?? 'Zustand wird geprüft …'
    default:
      return labels.idle
  }
}

/** Ein Klick wird nur angenommen, wenn der Knopf tatsächlich frei ist. */
export function acceptsClick(state: ActionState): boolean {
  return state === 'idle' || state === 'error'
}

export interface Transition {
  state: ActionState
  /** Soll die Aktion tatsächlich ausgelöst werden? */
  run: boolean
}

/**
 * Der Klick selbst. Entscheidend: der Übergang nach 'busy' passiert synchron,
 * im selben Ereignis wie der Klick – nicht erst, wenn die Anfrage losgeht.
 * Nur so kann ein zweiter Klick keine zweite Aktion starten.
 */
export function onClick(state: ActionState): Transition {
  if (!acceptsClick(state)) return { state, run: false }
  return { state: 'busy', run: true }
}

export type Outcome =
  | { kind: 'success' }
  | { kind: 'error'; message: string }
  | { kind: 'unclear'; message: string }

export function onOutcome(outcome: Outcome): ActionState {
  if (outcome.kind === 'success') return 'success'
  if (outcome.kind === 'unclear') return 'unclear'
  return 'error'
}
